import React, { useState } from "react";
import { Link } from "react-router-dom";
import { appointmentService } from "../api/services";

const SmartScheduling = () => {
  const [activeTab, setActiveTab] = useState("features");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    practice: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Create a demo appointment request
      await appointmentService.createAppointment({
        type: "demo_request",
        patientName: formData.name,
        email: formData.email,
        phone: formData.phone,
        practiceName: formData.practice,
        status: "pending",
        notes: "Demo request for Smart Scheduling module",
      });

      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        practice: "",
      });
    } catch (err) {
      setError(err.message || "Failed to submit demo request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-600 to-blue-500 py-16">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Smart Scheduling
              </h1>
              <p className="text-white text-xl mb-6">
                AI-powered scheduling system that optimizes appointment slots and reduces no-shows
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="#demo" className="bg-white text-indigo-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                  Request Demo
                </Link>
                <Link to="#pricing" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-indigo-600 font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                  View Pricing
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <img 
                src="/assets/smart-scheduling-hero.svg" 
                alt="Smart Scheduling" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-4xl font-bold text-indigo-600 mb-2">30%</h3>
              <p className="text-gray-700">Reduction in No-Shows</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-4xl font-bold text-indigo-600 mb-2">25%</h3>
              <p className="text-gray-700">More Appointments Booked</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-4xl font-bold text-indigo-600 mb-2">4.5 hrs</h3>
              <p className="text-gray-700">Staff Time Saved Weekly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="flex border-b border-gray-200 mb-8">
            <button 
              className={`py-2 px-4 font-medium text-lg ${activeTab === "features" ? "text-indigo-600 border-b-2 border-indigo-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("features")}
            >
              Features
            </button>
            <button 
              className={`py-2 px-4 font-medium text-lg ${activeTab === "how-it-works" ? "text-indigo-600 border-b-2 border-indigo-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("how-it-works")}
            >
              How It Works
            </button>
            <button 
              className={`py-2 px-4 font-medium text-lg ${activeTab === "testimonials" ? "text-indigo-600 border-b-2 border-indigo-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("testimonials")}
            >
              Testimonials
            </button>
          </div>

          {/* Features Tab */}
          {activeTab === "features" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">AI-Powered Optimization</h3>
                </div>
                <p className="text-gray-600">Our AI algorithm analyzes historical data to optimize appointment scheduling, ensuring maximum efficiency and productivity.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Smart Reminders</h3>
                </div>
                <p className="text-gray-600">Automated reminders sent at optimal times via text, email, or app notifications to reduce no-shows by up to 30%.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Online Booking</h3>
                </div>
                <p className="text-gray-600">24/7 self-service appointment booking with real-time availability and instant confirmation for patients.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Waitlist Management</h3>
                </div>
                <p className="text-gray-600">Automatically fill canceled appointments with patients from your waitlist, maximizing your schedule efficiency.</p>
              </div>
            </div>
          )}

          {/* How It Works Tab */}
          {activeTab === "how-it-works" && (
            <div className="space-y-12">
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
                  <div className="bg-indigo-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <span className="text-indigo-600 font-bold text-xl">1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Data Analysis</h3>
                  <p className="text-gray-600">Our AI analyzes your historical appointment data, including patient preferences, cancellation patterns, and peak hours to build a predictive model.</p>
                </div>
                <div className="md:w-1/2">
                  <img src="/assets/smart-scheduling-step1.svg" alt="Data Analysis" className="rounded-lg shadow-md" />
                </div>
              </div>

              <div className="flex flex-col md:flex-row-reverse items-center">
                <div className="md:w-1/2 mb-6 md:mb-0 md:pl-8">
                  <div className="bg-indigo-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <span className="text-indigo-600 font-bold text-xl">2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Optimized Scheduling</h3>
                  <p className="text-gray-600">Based on the analysis, our system creates an optimized scheduling template that maximizes provider productivity and patient convenience.</p>
                </div>
                <div className="md:w-1/2">
                  <img src="/assets/smart-scheduling-step2.svg" alt="Optimized Scheduling" className="rounded-lg shadow-md" />
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
                  <div className="bg-indigo-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <span className="text-indigo-600 font-bold text-xl">3</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Smart Reminders</h3>
                  <p className="text-gray-600">The system automatically sends personalized reminders at the optimal times to minimize no-shows, with escalating reminders for high-risk appointments.</p>
                </div>
                <div className="md:w-1/2">
                  <img src="/assets/smart-scheduling-step3.svg" alt="Smart Reminders" className="rounded-lg shadow-md" />
                </div>
              </div>
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === "testimonials" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <img src="/assets/testimonial-1.png" alt="Dr. Sarah Johnson" className="w-16 h-16 rounded-full mr-4" />
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">Dr. Sarah Johnson</h4>
                    <p className="text-gray-600">Bright Smile Dental</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">"The Smart Scheduling system has transformed our practice. We've reduced no-shows by 35% and our staff saves hours each week that used to be spent on managing the schedule."</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <img src="/assets/testimonial-2.png" alt="Dr. Michael Chen" className="w-16 h-16 rounded-full mr-4" />
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">Dr. Michael Chen</h4>
                    <p className="text-gray-600">Advanced Dental Care</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">"The AI optimization has helped us fit in 4-5 more appointments each day without feeling rushed. Our revenue has increased by 20% since implementing this system."</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Demo Request Section */}
      <section id="demo" className="py-16 bg-indigo-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Experience Smart Scheduling in Action</h2>
            <p className="text-gray-600 mb-8">Schedule a personalized demo to see how our AI-powered scheduling system can transform your practice.</p>
            
            {success ? (
              <div className="bg-green-50 p-6 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">Demo Request Submitted!</h3>
                <p className="text-green-600">We'll contact you within 24 hours to schedule your personalized demo.</p>
                <button 
                  onClick={() => setSuccess(false)}
                  className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Full Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email Address</label>
                    <input 
                      type="email" 
                      id="email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">Phone Number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                  <div>
                    <label htmlFor="practice" className="block text-gray-700 font-medium mb-2">Practice Name</label>
                    <input 
                      type="text" 
                      id="practice" 
                      value={formData.practice}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  disabled={loading}
                  className={`w-full bg-indigo-600 text-white font-bold py-3 px-6 rounded-lg transition duration-300 ${
                    loading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-indigo-700'
                  }`}
                >
                  {loading ? 'Submitting...' : 'Request Demo'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Simple, Transparent Pricing</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-indigo-600 p-6 text-center">
                <h3 className="text-2xl font-bold text-white">Smart Scheduling Module</h3>
                <p className="text-indigo-100 mt-2">Included with all EzzyDental plans</p>
              </div>
              
              <div className="p-6">
                <div className="flex justify-center mb-8">
                  <div className="text-center">
                    <span className="text-gray-400 text-lg">Starting at</span>
                    <div className="flex items-center justify-center">
                      <span className="text-5xl font-bold text-gray-800">$99</span>
                      <span className="text-gray-600 ml-2">/month per office</span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">AI-Powered Optimization</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Smart Reminders</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Online Booking</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Waitlist Management</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Provider Preferences</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700">Analytics Dashboard</span>
                  </div>
                </div>
                
                <div className="text-center">
                  <Link to="#demo" className="inline-block bg-indigo-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-indigo-700 transition duration-300">
                    Get Started
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Frequently Asked Questions</h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-800 mb-2">How does the AI optimize scheduling?</h3>
              <p className="text-gray-600">Our AI analyzes historical data including appointment duration, provider preferences, patient history, and no-show patterns to create the most efficient schedule possible.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Can patients book their own appointments?</h3>
              <p className="text-gray-600">Yes, patients can book appointments 24/7 through our online portal or mobile app. The system will only show available slots based on your preferences and the AI optimization.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-800 mb-2">How does the waitlist management work?</h3>
              <p className="text-gray-600">When an appointment is canceled, the system automatically identifies patients on the waitlist who match the criteria for that slot and sends them a notification to book the newly available time.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Can I customize the reminder messages?</h3>
              <p className="text-gray-600">Absolutely! You can customize all reminder templates, timing, and delivery methods (SMS, email, app notification) to match your practice's branding and communication style.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-indigo-600 py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Scheduling?</h2>
          <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of dental practices that have optimized their scheduling, reduced no-shows, and increased revenue with our Smart Scheduling system.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="#demo" className="bg-white text-indigo-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition duration-300">
              Request Demo
            </Link>
            <Link to="/pricing" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-indigo-600 font-bold py-3 px-8 rounded-lg transition duration-300">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmartScheduling; 